import { useTranslation } from 'react-i18next';
import { CheckCircle2, TriangleAlert } from 'lucide-react';
import { formatDateTime } from '@/lib/i18n';
import { useApi } from '@/Hooks/useApi';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Icon } from '@/components/ui/icon';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { useAutoRefresh, type RiskHealth } from './lib';

const POLL_MS = 30_000;

/**
 * "Is detection working" — not "what did it find".
 *
 * ADR-0017: silence is the expected healthy state for this feature, so an
 * empty case queue and a broken sweep look identical from the queue alone.
 * This is the screen that tells them apart, and its most important sentence
 * is the one for a sweep that finished cleanly, on a venue with rules
 * enabled, having scanned zero facts — the exact signature of an unbound
 * tenant read, reported here as broken rather than as quiet.
 */
export default function HealthTab() {
  const { t, i18n } = useTranslation();
  const { data, loading, error, reload } = useApi<RiskHealth>('/risk/health');

  useAutoRefresh(reload, { enabled: true, intervalMs: POLL_MS });

  if (loading && !data) return <LoadingState label={t('common.loading')} />;
  if (error && !data) return <ErrorState message={error || t('risk.health.loadFailed')} onRetry={reload} />;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <Card className={data.healthy ? undefined : 'border-red-300 dark:border-red-900'}>
        <CardContent className="flex flex-wrap items-center gap-3 p-5">
          <Icon
            as={data.healthy ? CheckCircle2 : TriangleAlert}
            size="lg"
            className={data.healthy ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}
          />
          <div>
            <p className="text-lg font-semibold">
              {t(data.healthy ? 'risk.health.healthy' : 'risk.health.broken')}
            </p>
            <p className="text-sm text-ink-muted">
              {t(data.healthy ? 'risk.health.healthyHint' : 'risk.health.brokenHint')}
            </p>
          </div>
        </CardContent>
      </Card>

      {data.problems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('risk.health.problems')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {data.problems.map((problem, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Icon as={TriangleAlert} size="sm" className="mt-0.5 shrink-0 text-amber-500" />
                  <span>{problem}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Metric
          label={t('risk.health.enabledRules')}
          value={`${data.enabledRules} / ${data.totalRules}`}
        />
        <Metric label={t('risk.health.reviewers')} value={String(data.reviewers)} />
        <Metric label={t('risk.health.openEvents')} value={String(data.openEvents)} />
        <Metric
          label={t('risk.health.lastSweepStartedAt')}
          value={data.lastSweepStartedAt ? formatDateTime(data.lastSweepStartedAt, i18n.language) : '—'}
        />
        <Metric
          label={t('risk.health.lastSuccessfulSweepAt')}
          value={
            data.lastSuccessfulSweepAt ? formatDateTime(data.lastSuccessfulSweepAt, i18n.language) : '—'
          }
        />
        <Metric
          label={t('risk.health.factsScannedLastSweep')}
          value={data.factsScannedLastSweep === null ? '—' : String(data.factsScannedLastSweep)}
          warn={data.enabledRules > 0 && data.factsScannedLastSweep === 0}
        />
      </div>
    </div>
  );
}

function Metric({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs uppercase tracking-wide rtl:tracking-normal text-ink-muted">{label}</p>
        <p className="mt-1 flex items-center gap-2 text-xl font-semibold tabular-nums">
          {value}
          {warn && <Badge tone="danger">!</Badge>}
        </p>
      </CardContent>
    </Card>
  );
}
